import React, { useEffect, useState, useCallback } from "react";
import { Route, Routes } from "react-router-dom";

import { initKeycloak, isLoggedIn } from "./services/authService";

import BaseLayout from "./components/layout/BaseLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import HomePage from "./pages/Home";
import ProjectPage from "./pages/Project";
import LoginPage from "./pages/Login";
import ContactPage from "./pages/Contact";
import DashboardPage from "./pages/Dashboard";
import CaseManagement from "./pages/CaseManagement";
import UserProfile from "./pages/UserProfile";
import ProtectedLayout from "./components/layout/ProtectedLayout";
import CaseDetails from "./pages/CaseDetails";
import { useLoaderProvider } from "./contexts/LoaderContext";
import { routes } from "./routes";
import CaseReport from "./pages/CaseReport";
import LoadingSpinner from "./components/common/LoadingSpinner";
import { AUTHORITIES } from "./config/constants";
import UserReport from "./pages/UserReport";
import Register from "./pages/Register";
import CreateUserPage from "./pages/CreateUser";
import AmiloidosiCardiacaPage from "./pages/malattie/AmiloidosiCardiaca";
import MalattiaFabryPage from "./pages/malattie/MalattiaFabryPage";
import CardiomiopatiaIpertroficaOstruttivaPage from "./pages/malattie/CardiomiopatiaIpertroficaOstruttivaPage";
import IpercolesterolemiaFamiliarePage from "./pages/malattie/IpercolesterolemiaFamiliarePage";
import SymptomsPage from "./pages/Symptoms";
import PatientPage from "./pages/Patient";
import PrivacyPolicyPage from "./pages/PrivacyPolicy";
import CookiePolicyPage from "./pages/CookiePolicy";
import TermsAndConditionsPage from "./pages/TermsAndConditions";
import { adminUserService } from "./services/adminUserService";
import { doctorsService } from "./services/doctorsService";
import { useAuth } from "./contexts/authContext";

const App: React.FC = () => {
  const { loaderState, showLoader, hideLoader } = useLoaderProvider();
  const { loggedDoctor, loginContext } = useAuth();
  const [keycloakInitialized, setKeycloakInitialized] = useState(false);

  const initializeKeycloak = useCallback(async () => {
    try {
      showLoader("Caricamento...");
      await initKeycloak();
      if (isLoggedIn() && !loggedDoctor) {
        await adminUserService.getAccount();
        const doctor = await doctorsService.getLoggedDoctor();
        loginContext(doctor);
      }
    } catch (error) {
      console.error("Errore durante il caricamento:", error);
    } finally {
      setKeycloakInitialized(true);
      hideLoader();
    }
  }, [hideLoader, loggedDoctor, loginContext, showLoader]);

  useEffect(() => {
    initializeKeycloak();
  }, [initializeKeycloak]);

  return (
    <>
      <LoadingSpinner
        showLoader={loaderState.showLoader}
        tip={loaderState.message}
        size={loaderState.size}
      />
      {keycloakInitialized && (
        <Routes>
          <Route element={<BaseLayout />}>
            <Route path={`${routes.HOME}`} element={<HomePage />} />
            <Route path={`${routes.PROJECT}`} element={<ProjectPage />} />
            <Route path={`${routes.LOGIN}`} element={<LoginPage />} />
            <Route path={`${routes.CONTACT}`} element={<ContactPage />} />
            <Route path={`${routes.SYMPTOMS}`} element={<SymptomsPage />} />
            <Route path={`${routes.REGISTER}`} element={<Register />} />
            <Route
              path={`${routes.PATIENT_CONTACT}`}
              element={<PatientPage />}
            />
            <Route
              path={`${routes.AMILOIDOSI_CARDIACA}`}
              element={<AmiloidosiCardiacaPage />}
            />
            <Route
              path={`${routes.MALATTIA_DI_FABRY}`}
              element={<MalattiaFabryPage />}
            />
            <Route
              path={`${routes.CARDIOMIOPATIA_IPERTROFICA_OSTRUTTIVA}`}
              element={<CardiomiopatiaIpertroficaOstruttivaPage />}
            />
            <Route
              path={`${routes.IPERCOLESTEROLEMIA_FAMILIARE}`}
              element={<IpercolesterolemiaFamiliarePage />}
            />
            <Route
              path={`${routes.PRIVACY_POLICY}`}
              element={<PrivacyPolicyPage />}
            />
            <Route
              path={`${routes.COOKIE_POLICY}`}
              element={<CookiePolicyPage />}
            />
            <Route
              path={`${routes.TERMS_AND_CONDITIONS}`}
              element={<TermsAndConditionsPage />}
            />
          </Route>
          <Route element={<ProtectedLayout />}>
            <Route
              path={`${routes.DASHBOARD}`}
              element={
                <ProtectedRoute
                  capabilities={[
                    AUTHORITIES.MANAGER,
                    AUTHORITIES.ASL_DOCTOR,
                    AUTHORITIES.SPECIALIST,
                  ]}
                >
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={`${routes.CASE_REPORT}`}
              element={
                <ProtectedRoute
                  capabilities={[
                    AUTHORITIES.ASL_DOCTOR,
                    AUTHORITIES.SPECIALIST,
                  ]}
                >
                  <CaseReport />
                </ProtectedRoute>
              }
            />
            <Route
              path={`${routes.USER_REPORT}`}
              element={
                <ProtectedRoute
                  capabilities={[AUTHORITIES.MANAGER, AUTHORITIES.ADMIN]}
                >
                  <UserReport />
                </ProtectedRoute>
              }
            />
            <Route
              path={`${routes.CREATE_USER}`}
              element={
                <ProtectedRoute
                  capabilities={[AUTHORITIES.MANAGER, AUTHORITIES.ADMIN]}
                >
                  <CreateUserPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={`${routes.CASE_DETAILS}`}
              element={
                <ProtectedRoute
                  capabilities={[
                    AUTHORITIES.ASL_DOCTOR,
                    AUTHORITIES.SPECIALIST,
                  ]}
                >
                  <CaseDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path={`${routes.CASE_MANAGEMENT}`}
              element={
                <ProtectedRoute
                  capabilities={[
                    AUTHORITIES.ASL_DOCTOR,
                    AUTHORITIES.SPECIALIST,
                  ]}
                >
                  <CaseManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path={`${routes.PROFILE}`}
              element={
                <ProtectedRoute
                  capabilities={[
                    AUTHORITIES.ASL_DOCTOR,
                    AUTHORITIES.SPECIALIST,
                  ]}
                >
                  <UserProfile />
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
      )}
    </>
  );
};

export default App;
